/**
 * Surfacer-mode right-panel: verifier surface for missed-finding review.
 *
 * Renders the candidates returned by /api/reader/cohort/<id>/study/<uid>/surfacer
 * as a vertical list of expanded rows — anatomy, finding name, confidence,
 * category, report excerpt, and a 7-category feedback control (true miss …
 * in other report). A true miss or inconsequential verdict also takes an
 * "Actionable?" Yes/No call and a short note. The whole card is clickable;
 * clicking jumps the viewer to top_slice and scrolls the original report to the
 * matching excerpt.
 *
 * Verdicts carry across versions. Each report-matching version (prompt / LLM /
 * cutoffs) is its own replay cohort over the same image predictions, and a verdict
 * is about (study, finding, report), so the backend hands back the latest verdict
 * anyone gave this study+finding under any version of the family as
 * `prior_verdict`. Such a card is "already judged": it renders compact and muted
 * with that verdict, who gave it and under which version, and sits after the
 * cards nobody has judged in its group. Clicking a verdict on it writes the
 * reader's own verdict in this cohort, as on any card; a card the reader judged
 * here renders as before. The header's Version picker (from the response's
 * `versions`) opens this study under another version of the family; "Only
 * unjudged" makes Next case skip studies with nothing left to judge.
 *
 * Keyboard (scoped to this panel — the handler sits on the panel root, so keys
 * only act while focus is inside it, and never while typing in the note field):
 *   1–7  set the verdict on the focused card (else the selected one), in button
 *        order (1 True miss … 6 Report mentions, 7 In other report); pressing
 *        the active verdict's key clears it to pending, same as clicking it
 *        again.
 *        Until the reader picks a card, the selected card is the first one
 *        nobody has judged, top to bottom. When a key gives a verdict to a card
 *        nobody had judged, selection (and focus, if it was on that card) moves
 *        to the next such card below it, wrapping to the first one left; with
 *        none left it stays put. Changing or clearing a verdict, or overriding
 *        an already-judged card, never moves it. Already-judged cards are
 *        skipped by that move but still take keys when focused or clicked.
 *   N    open the next unreviewed case in this worklist (with "Only unjudged"
 *        on, the next one with a finding nobody has judged).
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { isAdminAtom, userIdAtom } from "../../atoms/authAtoms";
import {
  adjudicationChip,
  isPredictedNoise,
  type PriorVerdict,
  type SurfacerCandidate,
  type SurfacerMatch,
  type SurfacerResponse,
} from "../../hooks/useSurfacerCandidates";
import {
  ACTIONABILITY_VERDICTS,
  NOTE_MAX_CHARS,
  useSurfacerDecisions,
  type DecisionDetails,
  type Verdict,
} from "../../hooks/useSurfacerDecisions";
import { useSurfacerMatches } from "../../hooks/useSurfacerMatches";
import { useAllSkippedStudies, useSkipCase, type SkipReason } from "../../hooks/useSkippedStudies";
import { useSurfacerNextCase } from "../../hooks/useSurfacerNextCase";
import { SkipReasonMenu } from "../SkipReasonMenu";
import SurfacerVersionPicker, { showVersionPicker } from "./SurfacerVersionPicker";
import { surfacerScoreboardPath } from "../../utils/surfacerScoreboard";

interface SurfacerPanelProps {
  data: SurfacerResponse;
  onFindingClick?: (topSliceUid: string) => void;
  onSelectExcerpt?: (excerpt: string) => void;
}

/* The anatomy badge is neutral. It used to be coloured by `category`, which made
 * one chip carry two unrelated meanings — text = anatomy, colour = clinical tier —
 * while the tier was already spelled out in its own chip beside it. The tier now
 * owns the colour; anatomy is just a label. */
const ANATOMY_BADGE_CLASS = "bg-surface-2 text-fg-muted border-border";

/* Clinical tier, coloured to match the worklist's SeverityBadge so the same case
 * reads the same in both views (Worklist.tsx SEVERITY_LABEL_CLASS).
 *
 * NOTE: those are raw Tailwind reds/oranges, not design-system tokens, and
 * CLAUDE.md's state-encoding table reserves red for "destructive / hard error
 * ONLY" and asks for non-hue cues. Matching the worklist therefore propagates an
 * off-system pattern. Kept deliberately for cross-view consistency — if the
 * worklist badge moves onto the non-hue encoding, move this with it. */
const CATEGORY_BADGE_CLASS: Record<SurfacerCandidate["category"], string> = {
  acute: "border-red-500/50 text-red-400 bg-red-500/15",
  prompt_workup: "border-orange-500/50 text-orange-400 bg-orange-500/15",
};

function confidenceClass(confidence: SurfacerCandidate["confidence"]): string {
  return confidence === "High"
    ? "bg-signal-tint text-signal-text-tint border-signal-stroke"
    : "bg-signal-ambient text-signal-text-tint border-signal-stroke-soft";
}

/* `prompt_workup` is the clinical_tier the backend sends; "Actionable" is a
 * display-only rename. Every other surface (verdict rows, config, the billable
 * filter) uses the raw tier name, so anyone comparing screen to data needs this
 * mapping — hence the tooltip on the chip. */
const CATEGORY_LABEL: Record<SurfacerCandidate["category"], string> = {
  acute: "Acute",
  prompt_workup: "Actionable",
};

const CATEGORY_TITLE: Record<SurfacerCandidate["category"], string> = {
  acute: "Clinical tier: acute — needs attention now",
  prompt_workup: "Clinical tier: prompt_workup — actionable, needs follow-up",
};

/**
 * The seven feedback categories a reader assigns to a surfaced candidate.
 * `pending` is the unreviewed default and has no button — clicking the active
 * category again clears back to `pending`.
 *
 * `VERDICT_META` is keyed by the non-`pending` Verdict union, so adding a verdict
 * without giving it a label/title fails to compile here — the rendered control
 * can never silently drop a category. `VERDICT_OPTIONS` derives the button list
 * from it in declaration (key) order, and that order IS the layout.
 *
 * ORDER = a single row read left→right as "how much did the AI get right":
 *
 *   True miss ─ Inconsequential ─ Could be right ─ Wrong name ─ Overcall ‖ Report mentions ─ In other report
 *   fully right ──────────────────────────────────────────────► wrong    ‖ our bug ──────── can't judge
 *
 * Two things this fixes. The old 2x3 grid put `true_miss` directly above
 * `report_mentions` — the two opposite answers on the "is it in the report" axis,
 * and the two most-clicked (30 and 15 of the first 73 verdicts), so the
 * highest-traffic pair was also the easiest to misclick. And `report_mentions`
 * isn't a clinical judgement at all: it says OUR extraction failed, not something
 * about the patient. It sits behind PIPELINE_DIVIDER — a solid 2px rule in
 * the same grey as the perimeter, so only the extra width marks the fence.
 *
 * `other_report` (key 7) is the other statement about the report rather than
 * the patient: the report sends this region to a separate report (e.g. "see CT
 * chest report"), which the reader can't see, so the finding can't be judged as
 * missed. It sits after Report mentions behind the same fence, and is never
 * scored as a true miss. Appended last so keys 1–6 keep their meaning.
 */
const VERDICT_META: Record<Exclude<Verdict, "pending">, { label: string; title: string }> = {
  true_miss: {
    label: "True miss",
    title: "Real finding the radiologist missed — AI correct",
  },
  inconsequential: {
    label: "Inconsequential",
    title:
      "Real finding, but clinically inconsequential — reasonable to omit " +
      "(e.g. calcified granuloma, prostatic calcification)",
  },
  ambiguous: {
    label: "Could be right",
    title: "Ambiguous — either read is defensible",
  },
  wrong_name: {
    label: "Wrong name",
    title:
      "Real abnormality at the right location — but the model labeled it the " +
      "wrong finding (e.g. an opacity called a pulmonary nodule)",
  },
  overcall: {
    label: "Overcall",
    title: "Finding isn't there; model overcalled",
  },
  report_mentions: {
    label: "Report mentions",
    title:
      "The report DOES state this finding — our extraction missed it. Not a " +
      "clinical judgement about the case; a defect report about the comparison.",
  },
  other_report: {
    label: "In other report",
    title:
      "The report sends this region to a separate report (e.g. 'see CT chest " +
      "report'), so it can't be judged as missed.",
  },
};

/** Verdicts about the report / comparison rather than the finding. Rendered at
 * the end of the row, fenced off from the five clinical judgements. */
const PIPELINE_VERDICTS = new Set<Verdict>(["report_mentions", "other_report"]);

/** The fence between the clinical verdicts and the pipeline one. `border-strong`
 * at 2px — the same colour as the control's perimeter and the 1px hairlines
 * between buttons, so only the extra width marks the fence. */
const PIPELINE_DIVIDER = "border-l-2 border-l-border-strong";

/**
 * Hue-coded verdicts, under the verdict-hue-scale exception in `CLAUDE.md`'s
 * state-encoding table (added for #896). Green = the AI was right and the
 * radiologist missed it, red = the AI was wrong, orange = our extraction was
 * wrong. The three uncoloured clinical verdicts (Inconsequential, Could be
 * right, Wrong name) are the in-between judgements, which is its own signal; In
 * other report is uncoloured too, since it says neither who was right nor that
 * we were wrong.
 *
 * The hue is REDUNDANT, per condition 1 of that exception: every button carries
 * its own explicit text label, and that label is what answers the question. A
 * reader with deuteranopia loses a scanning shortcut, not information — so don't
 * drop a label on the assumption the colour covers it.
 *
 * Background only — no per-verdict text colour, so the hue is a wash behind the
 * row rather than seven differently-coloured words competing for attention.
 * Labels are a uniform `text-white` across all seven buttons and both states.
 */
/*
 * Each entry is `bg-bg-2` (opaque #0E0E12) PLUS a flat gradient carrying the hue.
 * The opaque base is load-bearing: `border-strong` — the group container's
 * background — is `rgba(255,255,255,0.14)`, i.e. a white overlay. A bare
 * `bg-green-500/5` is transparent, so it composited over that white and the
 * button rendered LIGHTER than its opaque near-black neighbours. Painting the
 * hue as a background-image over an opaque background-color keeps it dark.
 *
 * To tune: change the two matching opacity values in each string. They must stay
 * equal — a flat gradient is just a way to layer one colour over another.
 */
const VERDICT_TINT_BG: Partial<
  Record<Exclude<Verdict, "pending">, { idle: string; active: string }>
> = {
  true_miss: {
    idle:
      "bg-bg-2 bg-gradient-to-r from-green-500/[0.08] to-green-500/[0.08] " +
      "hover:from-green-500/[0.14] hover:to-green-500/[0.14]",
    active: "bg-bg-2 bg-gradient-to-r from-green-500/30 to-green-500/30",
  },
  overcall: {
    idle:
      "bg-bg-2 bg-gradient-to-r from-red-500/[0.08] to-red-500/[0.08] " +
      "hover:from-red-500/[0.14] hover:to-red-500/[0.14]",
    active: "bg-bg-2 bg-gradient-to-r from-red-500/30 to-red-500/30",
  },
  report_mentions: {
    idle:
      "bg-bg-2 bg-gradient-to-r from-orange-500/[0.08] to-orange-500/[0.08] " +
      "hover:from-orange-500/[0.14] hover:to-orange-500/[0.14]",
    active: "bg-bg-2 bg-gradient-to-r from-orange-500/30 to-orange-500/30",
  },
};

const VERDICT_OPTIONS = (Object.keys(VERDICT_META) as Exclude<Verdict, "pending">[]).map(
  (value) => ({ value, ...VERDICT_META[value] }),
);

/** Button label of a verdict; `pending` (no button) reads as itself. */
function verdictLabel(verdict: Verdict): string {
  return verdict === "pending" ? verdict : VERDICT_META[verdict].label;
}

/** The fence goes before the first pipeline verdict only, not between them. */
const FIRST_PIPELINE_INDEX = VERDICT_OPTIONS.findIndex((o) => PIPELINE_VERDICTS.has(o.value));

/** Number key → verdict, in button order ("1" = the leftmost button). */
function verdictForKey(key: string): Exclude<Verdict, "pending"> | null {
  const index = Number(key) - 1;
  return /^[1-9]$/.test(key) && index < VERDICT_OPTIONS.length
    ? VERDICT_OPTIONS[index].value
    : null;
}

const NEXT_CASE_KEYS = new Set(["n", "N"]);

/** Panel shortcuts never fire while the reader is typing (the note field). */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable
  );
}

/* How a report excerpt is labelled. The device's own quote (the adjudicator's
 * verified evidence quote, or the matcher's excerpt) is the report's words about
 * this finding. The heuristic is the backend's keyword guess at the nearest
 * section, so it says so. A backend older than `report_excerpt_source` only had
 * the heuristic. */
function isDeviceQuote(source: SurfacerCandidate["report_excerpt_source"]): boolean {
  return source === "adjudicator" || source === "matcher";
}

const EXCERPT_LABEL_CLASS =
  "text-[9px] font-semibold uppercase tracking-wider text-fg-faint whitespace-nowrap";

/* Same chip-button shape as the matched section's "Wrong" flag. Selected uses the
 * `signal-tint` cue from the state-encoding table; the Yes/No label carries the
 * meaning, so there is no hue. */
const TOGGLE_BASE =
  "shrink-0 px-1.5 py-0.5 rounded-xs text-[9px] font-semibold uppercase tracking-wider border " +
  "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal";
const TOGGLE_ACTIVE = "bg-signal-tint text-signal-text-tint border-signal-stroke";
const TOGGLE_IDLE =
  "bg-transparent text-fg-faint border-border hover:text-fg-dim hover:border-border-strong";

const NO_DETAILS: DecisionDetails = {};

interface ActionabilityControlsProps {
  details: DecisionDetails;
  onChange: (patch: DecisionDetails) => void;
}

/**
 * "Actionable?" Yes/No plus an optional note, shown under the verdict control
 * once a true miss or inconsequential verdict is chosen. Clicking the selected
 * answer again clears it, like the verdict buttons. The note saves on blur or
 * Enter. The input is keyed on the stored note, so it re-seeds when the stored
 * value changes, including a rollback after a failed save.
 */
const ActionabilityControls: React.FC<ActionabilityControlsProps> = ({ details, onChange }) => {
  const saveNote = (input: HTMLInputElement) => {
    const note = input.value.trim();
    if (note !== (details.note ?? "")) onChange({ note: note || undefined });
  };

  return (
    // Stop clicks and keys here so the card's jump-to-slice never fires, and its
    // Enter/Space handler can't swallow a space typed into the note.
    <div
      className="flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div role="group" aria-label="Actionable?" className="flex items-center gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-fg-dim">
          Actionable?
        </span>
        {([true, false] as const).map((value) => {
          const active = details.actionable === value;
          return (
            <button
              key={String(value)}
              type="button"
              aria-pressed={active}
              onClick={() => onChange({ actionable: active ? undefined : value })}
              className={`${TOGGLE_BASE} ${active ? TOGGLE_ACTIVE : TOGGLE_IDLE}`}
            >
              {value ? "Yes" : "No"}
            </button>
          );
        })}
      </div>
      <input
        key={details.note ?? ""}
        type="text"
        defaultValue={details.note ?? ""}
        maxLength={NOTE_MAX_CHARS}
        placeholder="Note (optional)"
        aria-label="Note"
        onBlur={(e) => saveNote(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="flex-1 min-w-0 px-1.5 py-0.5 text-[11px] bg-bg border border-border-strong rounded-xs text-fg placeholder-fg-faint focus:outline-none focus:border-primary/50"
      />
    </div>
  );
};

interface VerdictControlProps {
  verdict: Verdict;
  onSetVerdict: (verdict: Verdict) => void;
}

/**
 * The seven-verdict segmented control. Roving tabindex: one tab stop into the
 * group, arrow keys move between categories. The active verdict (or the first
 * button, when pending) is the entry point.
 */
const VerdictControl: React.FC<VerdictControlProps> = ({ verdict, onSetVerdict }) => {
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const activeIndex = VERDICT_OPTIONS.findIndex((o) => o.value === verdict);
  const [focusedIndex, setFocusedIndex] = React.useState(activeIndex >= 0 ? activeIndex : 0);

  const handleGroupKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // The panel's verdict and next-case keys pass through to its root handler.
    if (verdictForKey(e.key) || NEXT_CASE_KEYS.has(e.key)) return;
    // Stop every other key here so the card's Enter/Space jump-to-slice handler
    // can't also fire while the reader is driving the control by keyboard.
    e.stopPropagation();
    const n = VERDICT_OPTIONS.length;
    // Single row: every arrow steps by one. Up/Down used to move by `cols` on the
    // 2x3 grid; on one row that arithmetic lands back on the same button, so the
    // vertical keys would silently do nothing.
    let next: number;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (focusedIndex + 1) % n;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (focusedIndex - 1 + n) % n;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = n - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    setFocusedIndex(next);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div
      role="group"
      aria-label="Categorize finding"
      onKeyDown={handleGroupKeyDown}
      /* White 2px perimeter, matching PIPELINE_DIVIDER's weight and colour.
       *
       * The container background must stay DARK. It shows through the buttons'
       * semi-transparent tints, so `bg-white` here turned green/20 into a
       * pastel and left white labels barely legible on it. Dark base = the
       * tints read as dark washes and white text keeps its contrast. The cost
       * is that the 1px inter-button hairlines are `border-strong` grey rather
       * than white. `grid-cols-7`: one column per VERDICT_OPTIONS entry. */
      className="grid grid-cols-7 gap-px rounded border border-border-strong overflow-hidden bg-border-strong"
    >
      {VERDICT_OPTIONS.map((opt, i) => {
        const active = verdict === opt.value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              buttonRefs.current[i] = el;
            }}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFocusedIndex(i);
              onSetVerdict(active ? "pending" : opt.value);
            }}
            aria-pressed={active}
            tabIndex={i === focusedIndex ? 0 : -1}
            title={opt.title}
            /* `break-words` matters at seven columns: "Inconsequential" is a
             * single unbreakable word wider than its cell, so without it the
             * label clipped to "Inconsequentia" against the group's
             * `overflow-hidden`. */
            className={`px-1 py-1 text-[9px] font-semibold leading-tight text-center break-words transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-signal ${
              i === FIRST_PIPELINE_INDEX ? PIPELINE_DIVIDER : ""
            } text-white ${
              active
                ? (VERDICT_TINT_BG[opt.value]?.active ?? "bg-signal")
                : (VERDICT_TINT_BG[opt.value]?.idle ?? "bg-bg-2 hover:bg-surface-2")
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/** Short reviewer name for a reused verdict: "you", or the email's local part. */
export function reviewerShortName(reviewer: string, currentUser: string | null): string {
  if (currentUser && reviewer.toLowerCase() === currentUser.toLowerCase()) return "you";
  const at = reviewer.indexOf("@");
  return at > 0 ? reviewer.slice(0, at) : reviewer;
}

interface PriorVerdictLineProps {
  prior: PriorVerdict;
  currentCohortId: string;
  currentUser: string | null;
}

/** "Judged True miss · by jane · in <version>", then the note when there is one. */
const PriorVerdictLine: React.FC<PriorVerdictLineProps> = ({
  prior,
  currentCohortId,
  currentUser,
}) => {
  const label = VERDICT_META[prior.verdict]?.label ?? prior.verdict;
  const when = new Date(prior.timestamp);
  return (
    <div className="space-y-0.5" data-testid="surfacer-prior-verdict">
      <div
        className="text-[11px] text-fg-muted leading-snug"
        title={`${prior.reviewer}${Number.isNaN(when.getTime()) ? "" : ` · ${when.toLocaleString()}`}`}
      >
        <span className={EXCERPT_LABEL_CLASS}>Judged</span>{" "}
        <span className="font-semibold text-fg">{label}</span>
        {" · by "}
        {reviewerShortName(prior.reviewer, currentUser)}
        {" · "}
        {prior.cohort_id === currentCohortId ? (
          "this version"
        ) : (
          <>
            in <span className="font-mono text-[10px]">{prior.cohort_id}</span>
          </>
        )}
        {prior.actionable !== undefined && ` · actionable: ${prior.actionable ? "yes" : "no"}`}
      </div>
      {prior.note && <div className="text-[11px] italic text-fg-dim">“{prior.note}”</div>}
    </div>
  );
};

interface SurfacerRowProps {
  candidate: SurfacerCandidate;
  /** Backend `CONF_HIGH_MIN`; omitted by an older backend, and then the tooltip
   * says "the High cutoff" rather than inventing a number. */
  confidenceThreshold?: number;
  verdict: Verdict;
  details: DecisionDetails;
  /** A verdict reused from the version family, passed only while the reader has
   * none of their own here: the card then renders compact as already judged. */
  prior: PriorVerdict | null;
  currentCohortId: string;
  currentUser: string | null;
  onSetVerdict: (verdict: Verdict) => void;
  onSetDetails: (patch: DecisionDetails) => void;
  onJumpToSlice: () => void;
  /** Target of the panel's 1–7 keys when focus isn't on a card. */
  isSelected: boolean;
  onSelect: () => void;
}

const SurfacerRow: React.FC<SurfacerRowProps> = ({
  candidate,
  confidenceThreshold,
  verdict,
  details,
  prior,
  currentCohortId,
  currentUser,
  onSetVerdict,
  onSetDetails,
  onJumpToSlice,
  isSelected,
  onSelect,
}) => {
  const clickable = Boolean(candidate.top_slice || candidate.report_excerpt);
  const isMiss = verdict === "true_miss";
  const isNonMiss = verdict !== "pending" && !isMiss;
  // Adjudicator predicted this is likely already-reported or out-of-coverage
  // noise (>= a2z-reporter-v0.7.1). Down-ranked: greyed + a reason chip. The
  // radiologist's own verdict still governs the left border.
  const noiseChip = adjudicationChip(candidate.predicted_verdict, candidate.evidence_quote);
  const predictedNoise = noiseChip !== null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onJumpToSlice();
    }
  };

  const cardProps = {
    "data-surfacer-finding": candidate.finding,
    "data-testid": `surfacer-card-${candidate.finding}`,
    "aria-current": isSelected ? ("true" as const) : undefined,
    onClick: () => {
      onSelect();
      if (clickable) onJumpToSlice();
    },
    onFocus: onSelect,
    onKeyDown: handleKeyDown,
    role: clickable ? "button" : undefined,
    tabIndex: clickable ? 0 : undefined,
  };

  if (prior && verdict === "pending") {
    // Already judged in the family. Compact and muted (the state table's
    // opacity cue for "resolved"), dashed to read apart from a card the reader
    // judged themselves; selection still lifts it like any card. Hover or focus
    // restores full opacity so an override reads clearly.
    return (
      <div
        {...cardProps}
        data-judged="prior"
        className={`px-3 py-2 rounded-md border border-dashed border-border-strong transition-[opacity,colors] space-y-1.5 ${
          clickable ? "cursor-pointer" : ""
        } ${
          isSelected
            ? "bg-surface-2"
            : "bg-bg-2/40 opacity-60 hover:opacity-100 focus-within:opacity-100"
        }`}
      >
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-fg-dim leading-tight truncate">
            {candidate.display_name}
          </span>
          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-fg-faint">
            {candidate.anatomy}
          </span>
        </div>
        <PriorVerdictLine
          prior={prior}
          currentCohortId={currentCohortId}
          currentUser={currentUser}
        />
        <VerdictControl verdict={verdict} onSetVerdict={onSetVerdict} />
      </div>
    );
  }

  // Selected = the target of the panel's number keys. Encoded with the
  // state table's attention cue (solid border-strong + surface-2 lift), so it
  // reads apart from the verdict's left rule.
  const frameClass = isSelected ? "border-border-strong bg-surface-2" : "border-border bg-bg-2/55";
  const borderClass = isMiss
    ? `border-l-4 border-l-signal ${frameClass}`
    : isNonMiss
      ? `border-l-4 border-l-border-strong ${frameClass}`
      : `${frameClass} hover:border-signal-stroke hover:bg-surface-2`;

  return (
    <div
      {...cardProps}
      className={`px-3 py-2.5 rounded-md border transition-colors space-y-1.5 ${
        clickable ? "cursor-pointer" : ""
      } ${isNonMiss || predictedNoise ? "opacity-60" : ""} ${borderClass}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-[15px] font-semibold text-fg leading-tight flex-1 min-w-0">
          {candidate.display_name}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wider border ${ANATOMY_BADGE_CLASS}`}
        >
          {candidate.anatomy}
        </span>
        <span
          data-testid="surfacer-category-chip"
          className={`px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wide border ${
            CATEGORY_BADGE_CLASS[candidate.category]
          }`}
          title={CATEGORY_TITLE[candidate.category]}
        >
          {CATEGORY_LABEL[candidate.category]}
        </span>
        {/* "CONF" prefix disambiguates this from the cohort-rank chip beside it:
         * this one is an absolute cut on the AI's probability, that one is a rank
         * against peers. Unlabelled, "HIGH" and "P50" read as the same kind of
         * number and appear to contradict each other. A device that reports no
         * probability gets no chip. */}
        {candidate.confidence != null && (
          <span
            data-testid="surfacer-confidence-chip"
            className={`px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wide border ${confidenceClass(
              candidate.confidence,
            )}`}
            title={`Confidence band: ${candidate.confidence} — the AI's probability for this finding is ${
              candidate.confidence === "High" ? "at or above" : "below"
            }${confidenceThreshold != null ? ` ${confidenceThreshold}` : " the High cutoff"}. An absolute cut, not a comparison to other cases.`}
          >
            Conf {candidate.confidence}
          </span>
        )}
        <span
          className="px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wide border bg-bg-2/40"
          style={{
            color: `rgba(67, 97, 238, ${0.55 + (Math.min(candidate.advancement, 3) / 3) * 0.45})`,
            borderColor: `rgba(67, 97, 238, ${0.3 + (Math.min(candidate.advancement, 3) / 3) * 0.5})`,
          }}
          title={`Recommended ${candidate.recommended_op}; AI fired at ${candidate.fired_op} (${candidate.advancement} steps stricter)`}
        >
          OP +{candidate.advancement}
        </span>
        {candidate.percentile != null && (
          <span
            data-testid="surfacer-percentile-chip"
            className="px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wide bg-surface-2 text-fg-muted border border-border"
            title={`Cohort rank: this finding's probability is higher than ${Math.round(
              candidate.percentile * 100,
            )}% of other ${candidate.display_name.toLowerCase()} flags in this cohort. A relative rank, not a confidence score.`}
          >
            Rank P{Math.round(candidate.percentile * 100)}
          </span>
        )}
        {noiseChip && (
          <span
            className="px-1.5 py-[2px] rounded-xs text-[10px] font-bold uppercase tracking-wide border bg-bg-2/40 text-fg-faint border-border"
            title={noiseChip.title}
          >
            {noiseChip.label}
          </span>
        )}
      </div>

      {candidate.report_excerpt && isDeviceQuote(candidate.report_excerpt_source) ? (
        <div className="text-xs text-fg-dim leading-relaxed" data-testid="surfacer-excerpt">
          <span
            className={EXCERPT_LABEL_CLASS}
            title="Quoted from the report by the comparison model"
          >
            Report says
          </span>{" "}
          <span className="text-fg">"{candidate.report_excerpt}"</span>
        </div>
      ) : candidate.report_excerpt ? (
        <div className="text-xs text-fg-dim leading-relaxed" data-testid="surfacer-excerpt">
          <span
            className={EXCERPT_LABEL_CLASS}
            title="Picked by keyword match on the finding's anatomy, not quoted by the model"
          >
            Nearest section
          </span>{" "}
          <span className="text-fg">"{candidate.report_excerpt}"</span>{" "}
          <span className="text-fg font-semibold">— {candidate.miss_phrase}.</span>
        </div>
      ) : (
        <div className="text-xs italic text-fg-muted">
          {candidate.miss_phrase.charAt(0).toUpperCase() + candidate.miss_phrase.slice(1)} in the
          report.
        </div>
      )}
      {candidate.report_anchor && (
        <div className="text-[11px] text-fg-faint">
          ↓ section: <span className="text-fg-dim">{candidate.report_anchor}</span>
        </div>
      )}

      <VerdictControl verdict={verdict} onSetVerdict={onSetVerdict} />
      {ACTIONABILITY_VERDICTS.has(verdict) && (
        <ActionabilityControls details={details} onChange={onSetDetails} />
      )}
    </div>
  );
};

/**
 * Stable partition: cards nobody has judged first, then cards carrying a reused
 * verdict. Keyed on the server's `prior_verdict` only, so a card doesn't jump
 * when the reader gives it a verdict.
 */
export function unjudgedFirst(candidates: readonly SurfacerCandidate[]): SurfacerCandidate[] {
  return [
    ...candidates.filter((c) => !c.prior_verdict),
    ...candidates.filter((c) => !!c.prior_verdict),
  ];
}

const SurfacerPanel: React.FC<SurfacerPanelProps> = ({ data, onFindingClick, onSelectExcerpt }) => {
  const navigate = useNavigate();
  const isAdmin = useAtomValue(isAdminAtom);
  const currentUser = useAtomValue(userIdAtom);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const { decisions, details, setVerdict, setDetails, isError, saveError } = useSurfacerDecisions(
    data.cohort_id,
    data.study_uid,
  );

  // Case-level skip — set aside a case whose comparison is against an incomplete
  // report (e.g. a separate abd/pelvis report exists). Mirrors the worklist
  // right-click; flagging here lets the reviewer act while reading the report.
  const { data: skippedMap } = useAllSkippedStudies(true);
  const { setSkipped } = useSkipCase();
  const skipInfo = skippedMap?.[data.study_uid];
  const isSkipped = !!skipInfo;
  const [skipAnchor, setSkipAnchor] = React.useState<{ x: number; y: number } | null>(null);
  const skipBtnRef = React.useRef<HTMLButtonElement>(null);
  const openSkipMenu = () => {
    const r = skipBtnRef.current?.getBoundingClientRect();
    if (r) setSkipAnchor({ x: r.left, y: r.bottom + 4 });
  };

  const skipControl = (
    <>
      <button
        ref={skipBtnRef}
        type="button"
        onClick={openSkipMenu}
        data-testid="surfacer-skip-button"
        className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-xs border transition-colors ${
          isSkipped
            ? "border-warn/50 text-warn bg-warn-tint"
            : "border-border-strong text-fg-dim hover:text-fg"
        }`}
        title={
          isSkipped
            ? "This case is skipped — click to change"
            : "Skip this case (set aside; excluded from the worklist)"
        }
      >
        {isSkipped ? "Skipped" : "Skip case"}
      </button>
      {skipAnchor && (
        <SkipReasonMenu
          anchor={skipAnchor}
          isSkipped={isSkipped}
          currentReason={skipInfo?.reason}
          currentNote={skipInfo?.reason_note}
          onSkip={(reason: SkipReason, note?: string) =>
            setSkipped({
              cohortId: data.cohort_id,
              studyUid: data.study_uid,
              skipped: true,
              reason,
              reasonNote: note,
            })
          }
          onUnskip={() =>
            setSkipped({ cohortId: data.cohort_id, studyUid: data.study_uid, skipped: false })
          }
          onClose={() => setSkipAnchor(null)}
        />
      )}
    </>
  );

  // Done-reviewing action. Each verdict already persists optimistically, so this
  // is a navigation, not a batch save: it returns to the SAME cohort's worklist
  // (`data.cohort_id`, straight from the API response). A bare `/worklist`
  // intentionally has no selected list, so this is the in-context path back to
  // where the reader came from.
  const nextCase = useSurfacerNextCase(data.cohort_id, data.study_uid);
  const nextCaseControl = (
    <button
      type="button"
      onClick={nextCase.goToNext}
      disabled={!nextCase.next}
      data-testid="surfacer-next-case-button"
      aria-keyshortcuts="N"
      className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-xs border border-border-strong text-fg-dim hover:text-fg disabled:opacity-60 disabled:hover:text-fg-dim transition-colors"
      title={
        nextCase.next
          ? nextCase.onlyUnjudged
            ? "Open the next case with a finding nobody has judged (N)"
            : "Open the next unreviewed case in this worklist (N)"
          : nextCase.isLoading
            ? "Loading the worklist…"
            : nextCase.onlyUnjudged
              ? "No other case in this worklist has a finding nobody has judged"
              : "No other unreviewed case in this worklist"
      }
    >
      {nextCase.next || nextCase.isLoading ? "Next case" : "No more cases"}
    </button>
  );

  const doneControl = (
    <button
      type="button"
      onClick={() => navigate(`/worklist?cohort=${encodeURIComponent(data.cohort_id)}`)}
      data-testid="surfacer-done-button"
      className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-xs border border-signal bg-signal text-fg hover:opacity-90 transition-opacity"
      title="Done reviewing this case — return to this cohort's worklist"
    >
      Done
    </button>
  );

  // Queue bar under the header: the version picker (when this study has other
  // versions), the "Only unjudged" queue toggle with what it leaves, and, for
  // admins, the family scoreboard.
  const queueBar = (
    <div
      className="px-3 py-1.5 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid="surfacer-queue-bar"
    >
      {data.versions && showVersionPicker(data.versions) && (
        <SurfacerVersionPicker
          cohortId={data.cohort_id}
          studyUid={data.study_uid}
          versions={data.versions}
        />
      )}
      <button
        type="button"
        aria-pressed={nextCase.onlyUnjudged}
        onClick={() => nextCase.setOnlyUnjudged(!nextCase.onlyUnjudged)}
        data-testid="surfacer-only-unjudged"
        title={
          nextCase.onlyUnjudged
            ? "Next case skips cases where every finding has a verdict from someone, in any version. Click to go by your own verdicts only."
            : "Next case goes by your own verdicts. Click to also skip cases someone has already judged, in any version."
        }
        className={`${TOGGLE_BASE} ${nextCase.onlyUnjudged ? TOGGLE_ACTIVE : TOGGLE_IDLE}`}
      >
        Only unjudged
      </button>
      {nextCase.unjudgedLeft && (
        <span
          className="text-[11px] text-fg-dim"
          data-testid="surfacer-unjudged-left"
          title={`Findings nobody has judged, across ${nextCase.unjudgedLeft.cases} case${
            nextCase.unjudgedLeft.cases === 1 ? "" : "s"
          } in this worklist`}
        >
          <strong className="text-fg font-semibold">{nextCase.unjudgedLeft.findings}</strong>{" "}
          unjudged left
        </span>
      )}
      {isAdmin && (
        <a
          href={surfacerScoreboardPath(data.cohort_id)}
          target="_blank"
          rel="noreferrer"
          data-testid="surfacer-scoreboard-link"
          className="ml-auto text-[11px] text-signal-text-tint hover:underline"
          title="Compare every version of this cohort against the pooled verdicts (opens a new tab)"
        >
          Scoreboard ↗
        </a>
      )}
    </div>
  );

  const verdictFor = (finding: string): Verdict => decisions[finding] ?? "pending";
  // Nobody has judged it: no verdict of the reader's here, none reused from the family.
  const isUnjudged = (c: SurfacerCandidate) =>
    verdictFor(c.finding) === "pending" && !c.prior_verdict;

  // "Inline grey + sink": predicted-noise candidates (already-reported / not
  // covered, per the adjudicator) drop below a divider; likely-real misses stay
  // on top. Within each group, cards nobody has judged come before cards with a
  // reused verdict. Both partitions are stable, so the backend's
  // advancement/probability order holds within each.
  const likely = unjudgedFirst(
    data.candidates.filter((c) => !isPredictedNoise(c.predicted_verdict)),
  );
  const sunk = unjudgedFirst(data.candidates.filter((c) => isPredictedNoise(c.predicted_verdict)));
  const displayOrder = [...likely, ...sunk];

  // Target of the 1–7 keys when focus is in the panel but not on a card (e.g.
  // the header). Set by clicking or focusing a card; until then, and after the
  // picked card is judged with nothing left after it, the first card nobody has
  // judged.
  const [pickedFinding, setSelectedFinding] = React.useState<string | null>(null);
  const firstUnjudged = displayOrder.find(isUnjudged)?.finding ?? null;
  const selectedFinding = pickedFinding ?? firstUnjudged;

  /** The next card nobody has judged after `finding`, wrapping; never `finding`. */
  const nextUnjudgedAfter = (finding: string): string | null => {
    const start = displayOrder.findIndex((c) => c.finding === finding);
    for (let off = 1; off < displayOrder.length; off++) {
      const c = displayOrder[(start + off) % displayOrder.length];
      if (c.finding !== finding && isUnjudged(c)) return c.finding;
    }
    return null;
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (isTypingTarget(e.target)) return;
    if (NEXT_CASE_KEYS.has(e.key)) {
      // Stop here so viewer-level listeners on window/document don't also act.
      e.preventDefault();
      e.stopPropagation();
      nextCase.goToNext();
      return;
    }
    const verdict = verdictForKey(e.key);
    if (!verdict) return;
    const card =
      e.target instanceof Element ? e.target.closest<HTMLElement>("[data-surfacer-finding]") : null;
    const finding = card?.dataset.surfacerFinding ?? selectedFinding;
    const target = data.candidates.find((c) => c.finding === finding);
    if (!finding || !target) return;
    // Digits also jump phase tabs at the window level; this key is ours.
    e.preventDefault();
    e.stopPropagation();
    const nextVerdict = verdictFor(finding) === verdict ? "pending" : verdict;
    setVerdict(finding, nextVerdict);
    if (!isUnjudged(target) || nextVerdict === "pending") {
      setSelectedFinding(finding);
      return;
    }
    // A first verdict on a card nobody had judged: move on to the next one.
    const advance = nextUnjudgedAfter(finding);
    setSelectedFinding(advance ?? finding);
    if (advance && card) {
      const cards = panelRef.current?.querySelectorAll<HTMLElement>("[data-surfacer-finding]");
      Array.from(cards ?? [])
        .find((el) => el.dataset.surfacerFinding === advance)
        ?.focus();
    }
  };

  const reviewedCount = data.candidates.filter((c) => verdictFor(c.finding) !== "pending").length;
  const priorCount = data.candidates.filter(
    (c) => verdictFor(c.finding) === "pending" && !!c.prior_verdict,
  ).length;
  const pendingCount = data.candidates.length - reviewedCount - priorCount;

  const renderRow = (candidate: SurfacerCandidate) => (
    <SurfacerRow
      key={candidate.finding}
      candidate={candidate}
      confidenceThreshold={data.confidence_threshold}
      verdict={verdictFor(candidate.finding)}
      details={details[candidate.finding] ?? NO_DETAILS}
      prior={candidate.prior_verdict ?? null}
      currentCohortId={data.cohort_id}
      currentUser={currentUser}
      onSetVerdict={(v) => setVerdict(candidate.finding, v)}
      onSetDetails={(patch) => setDetails(candidate.finding, patch)}
      onJumpToSlice={() => {
        if (candidate.top_slice) onFindingClick?.(candidate.top_slice);
        if (candidate.report_excerpt) onSelectExcerpt?.(candidate.report_excerpt);
      }}
      isSelected={selectedFinding === candidate.finding}
      onSelect={() => setSelectedFinding(candidate.finding)}
    />
  );

  if (data.candidates.length === 0) {
    return (
      <div className="h-full flex flex-col" onKeyDown={handlePanelKeyDown}>
        <div className="px-3 py-2 border-b border-border flex items-center gap-2 bg-signal-ambient">
          <span className="text-[11px] font-bold uppercase tracking-widest text-signal-text-tint">
            AI Suggestions
          </span>
          <div className="ml-auto flex items-center gap-2">
            {nextCaseControl}
            {doneControl}
          </div>
        </div>
        {queueBar}
        <div className="flex-1 flex items-center justify-center text-center text-sm text-fg-faint px-6">
          No high-confidence missed findings on this study after filtering.
        </div>
      </div>
    );
  }

  return (
    <div ref={panelRef} className="h-full flex flex-col" onKeyDown={handlePanelKeyDown}>
      {/* Wraps rather than squeezing: with the "already judged" count the row no
       * longer fits a narrow sidebar, and a wrapped button row reads better than
       * counts broken over two lines. */}
      <div className="px-3 py-2 border-b border-border flex flex-wrap items-center gap-x-2 gap-y-1.5 bg-signal-ambient">
        <span className="text-[11px] font-bold uppercase tracking-widest text-signal-text-tint">
          AI Suggestions
        </span>
        <div className="ml-auto text-xs text-fg-dim flex items-center gap-2 whitespace-nowrap">
          <span>
            <strong className="text-fg font-semibold">{pendingCount}</strong> pending
          </span>
          <span className="text-fg-faint">·</span>
          <span>
            <strong className="text-fg font-semibold">{reviewedCount}</strong> reviewed
          </span>
          {priorCount > 0 && (
            <>
              <span className="text-fg-faint">·</span>
              <span title="Judged before, by you or another reviewer, in this or another version">
                <strong className="text-fg font-semibold">{priorCount}</strong> already judged
              </span>
            </>
          )}
          {isError && (
            <>
              <span className="text-fg-faint">·</span>
              {saveError?.unsupportedVerdict ? (
                /* The server rejected the verdict itself (a backend that predates
                 * it), so "click again" would fail the same way: say so. */
                <span
                  className="text-signal-text-tint font-semibold"
                  role="alert"
                  title={`Not saved and reverted: the server doesn't accept this category yet.${
                    saveError.detail ? ` Server said: ${saveError.detail}` : ""
                  }`}
                >
                  save failed: server doesn&apos;t accept &ldquo;
                  {verdictLabel(saveError.verdict)}
                  &rdquo; yet
                </span>
              ) : (
                <span
                  className="text-signal-text-tint font-semibold"
                  title="Your last change couldn't be saved and was reverted. Click the category again to retry."
                >
                  save failed
                </span>
              )}
            </>
          )}
        </div>
        {skipControl}
        {nextCaseControl}
        {doneControl}
      </div>
      {queueBar}

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {likely.map(renderRow)}
        {sunk.length > 0 && (
          <div className="flex items-center gap-2 pt-1 select-none">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-faint whitespace-nowrap">
              Likely reported / not covered ({sunk.length})
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>
        )}
        {sunk.map(renderRow)}
        {/* Gated here, not inside the component: hooks must run before any
         * early return, so mounting MatchedSection on a study with no matches
         * would still fire its GET /surfacer/matches. */}
        {(data.matched?.length ?? 0) > 0 && (
          <MatchedSection
            matched={data.matched ?? []}
            cohortId={data.cohort_id}
            studyUid={data.study_uid}
            onSelectExcerpt={onSelectExcerpt}
            onFindingClick={onFindingClick}
          />
        )}
      </div>
    </div>
  );
};

interface MatchedSectionProps {
  matched: SurfacerMatch[];
  cohortId: string;
  studyUid: string;
  onSelectExcerpt?: (excerpt: string) => void;
  onFindingClick?: (topSliceUid: string) => void;
}

/**
 * Findings the AI fired on that the report DOES mention — collapsed by default.
 *
 * There are more of these than there are surfaced findings (118 vs 76 on the
 * 48-study soma cohort), so it must never compete with the list above it: header
 * only until expanded.
 *
 * Match is implicit. A correct match needs no click; the reader only flags the
 * ones that are wrong, and a `wrong` flag means the report does NOT state this
 * finding — i.e. the comparator suppressed something that should have surfaced.
 * That is the only direct measurement of false suppression we have, since every
 * other signal covers only findings we chose to show.
 *
 * Expanding writes a per-study marker, because without it "0 wrong of 118" is
 * indistinguishable from "nobody scrolled down". The marker records that the
 * section was OPENED, nothing stronger.
 */
const MatchedSection: React.FC<MatchedSectionProps> = ({
  matched,
  cohortId,
  studyUid,
  onSelectExcerpt,
  onFindingClick,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const { matches, setMatchDecision, markOpened } = useSurfacerMatches(cohortId, studyUid);

  const wrongCount = matched.filter((m) => matches[m.finding] === "wrong").length;

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) markOpened();
  };

  return (
    <div className="mt-1 border-t border-border pt-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        data-testid="matched-section-toggle"
        /* Reads as an affordance, not a caption: signal-blue text at the same
         * size as the row labels, with a chevron big enough to see. At
         * `text-fg-faint` + 10px it looked like a divider nobody could click. */
        className="w-full flex items-center gap-2 px-1 py-1.5 rounded-xs text-blue-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal"
      >
        <span className="text-[13px] leading-none w-3 text-center">{expanded ? "▾" : "▸"}</span>
        <span className="text-[12px] font-bold uppercase tracking-wider">
          Matched · {matched.length}
        </span>
        {wrongCount > 0 && (
          <span
            className="text-[11px] font-bold uppercase tracking-wider text-red-300/80"
            title="Matches you flagged as wrong — the report does not actually state these"
          >
            {wrongCount} wrong
          </span>
        )}
        <span className="ml-auto text-[10px] text-fg-faint normal-case tracking-normal font-normal">
          {expanded ? "click a quote to jump to it" : "already in the report"}
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-px mt-1">
          {matched.map((m) => {
            const isWrong = matches[m.finding] === "wrong";
            return (
              /* Left rule carries the state, mirroring how a surfaced row uses
               * `border-l-4` for the reader's verdict. Solid green is the resting
               * state and means accepted — match is implicit, so every row is
               * green until someone says otherwise. Dashed red marks the ones
               * flagged wrong.
               *
               * Solid-vs-dashed is the non-hue cue, required by condition 1 of
               * the verdict-hue-scale exception in CLAUDE.md: red/green alone is
               * the colourblind-hostile pair, so the rule STYLE has to carry the
               * same information the colour does. Dashed also already means
               * "lower-confidence / secondary" in that table, which is what a
               * disputed match is. */
              <div
                key={m.finding}
                data-testid={`matched-row-${m.finding}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded-xs border-l-4 ${
                  isWrong
                    ? "border-l-red-700/70 border-dashed bg-red-500/[0.04]"
                    : "border-l-green-700/70 border-solid bg-bg-2"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {/* Jumps the viewer to the finding, same as a surfaced row.
                     * `top_slice` was previously typed and sent but unused. */}
                    {m.top_slice ? (
                      <button
                        type="button"
                        onClick={() => onFindingClick?.(m.top_slice as string)}
                        data-testid={`matched-jump-${m.finding}`}
                        title="Jump the viewer to this finding"
                        className="text-[12px] font-semibold text-fg-dim truncate text-left hover:text-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-signal"
                      >
                        {m.display_name}
                      </button>
                    ) : (
                      <span className="text-[12px] font-semibold text-fg-dim truncate">
                        {m.display_name}
                      </span>
                    )}
                    <span className="text-[9px] font-bold uppercase tracking-wider text-fg-faint shrink-0">
                      {m.anatomy}
                    </span>
                  </div>
                  {m.matched_quote && (
                    <button
                      type="button"
                      onClick={() => onSelectExcerpt?.(m.matched_quote as string)}
                      className="mt-0.5 text-left text-[11px] text-fg-faint italic hover:text-fg-dim focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-signal"
                      title="Jump to this line in the report"
                    >
                      “{m.matched_quote}”
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setMatchDecision(m.finding, isWrong ? "pending" : "wrong")}
                  aria-pressed={isWrong}
                  data-testid={`matched-wrong-${m.finding}`}
                  title={
                    isWrong
                      ? "Flagged wrong — click to clear"
                      : "The report does NOT state this finding — we suppressed it in error"
                  }
                  className={`shrink-0 px-1.5 py-0.5 rounded-xs text-[9px] font-semibold uppercase tracking-wider border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal ${
                    isWrong
                      ? "bg-red-500/25 text-red-300 border-red-500/60"
                      : "bg-transparent text-fg-faint border-border hover:text-fg-dim hover:border-border-strong"
                  }`}
                >
                  Wrong
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SurfacerPanel;
